import { captureException } from '@sentry/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
    CreateLifecycleInput,
    LifecycleActionExecutionRecord,
    LifecycleApiAction,
    LifecycleCollaboratorInput,
    LifecycleCollaboratorUpdateInput,
    lifecycleService,
    UpdateLifecycleHierarchyOrderInput,
    UpdateLifecycleInput,
} from '@/services';
import type { LifecycleInitiative } from '@/shared/components/lifecycles';
import { useUserStore, useWorkspaceStore } from '@/stores';

export const lifecycleQueryKeys = {
    all: ['lifecycles'] as const,
    collaborators: (workspaceId: string | undefined, lifecycleId: string | undefined) =>
        [...lifecycleQueryKeys.all, workspaceId ?? 'none', lifecycleId ?? 'none', 'collaborators'] as const,
    executions: (workspaceId: string | undefined, lifecycleId: string | undefined, actionId: string | undefined) =>
        [
            ...lifecycleQueryKeys.all,
            workspaceId ?? 'none',
            lifecycleId ?? 'none',
            'actions',
            actionId ?? 'none',
            'executions',
        ] as const,
    detail: (workspaceId: string | undefined, lifecycleId: string | undefined) =>
        [...lifecycleQueryKeys.all, workspaceId ?? 'none', lifecycleId ?? 'none'] as const,
    list: (workspaceId: string | undefined) => [...lifecycleQueryKeys.all, workspaceId ?? 'none', 'list'] as const,
};

const useActiveWorkspaceId = () => {
    const selectedWorkspaceId = useWorkspaceStore((state) => state.selectedWorkspace?.id);
    const activeWorkspaceId = useUserStore((state) => state.selectedUser?.activeWorkspace?.id);
    return selectedWorkspaceId ?? activeWorkspaceId;
};

export const useLifecycleList = (options: { enabled?: boolean } = {}) => {
    const workspaceId = useActiveWorkspaceId();
    return useQuery({
        enabled: options.enabled !== false && Boolean(workspaceId),
        queryFn: () => lifecycleService.list({ limit: 100 }),
        queryKey: lifecycleQueryKeys.list(workspaceId),
        staleTime: 30_000,
    });
};

export const useLifecycleDetail = (lifecycleId?: string, options: { enabled?: boolean } = {}) => {
    const workspaceId = useActiveWorkspaceId();
    return useQuery({
        enabled: options.enabled !== false && Boolean(workspaceId && lifecycleId),
        queryFn: () => lifecycleService.getById(lifecycleId as string),
        queryKey: lifecycleQueryKeys.detail(workspaceId, lifecycleId),
        staleTime: 15_000,
    });
};

export const useCreateLifecycle = () => {
    const workspaceId = useActiveWorkspaceId();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: CreateLifecycleInput) => lifecycleService.create(input),
        onError: (error) => captureException(error),
        onSuccess: async (lifecycle) => {
            queryClient.setQueryData(lifecycleQueryKeys.detail(workspaceId, lifecycle.id), lifecycle);
            await queryClient.invalidateQueries({ queryKey: lifecycleQueryKeys.list(workspaceId) });
        },
    });
};

export const useUpdateLifecycle = (lifecycleId?: string) => {
    const workspaceId = useActiveWorkspaceId();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: UpdateLifecycleInput) => lifecycleService.update(lifecycleId as string, input),
        onError: (error) => captureException(error),
        onSuccess: async (lifecycle) => {
            queryClient.setQueryData(lifecycleQueryKeys.detail(workspaceId, lifecycleId), lifecycle);
            await queryClient.invalidateQueries({ queryKey: lifecycleQueryKeys.list(workspaceId) });
        },
    });
};

export const useSetLifecycleFavorite = (lifecycleId?: string) => {
    const workspaceId = useActiveWorkspaceId();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (favorited: boolean) => lifecycleService.setFavorite(lifecycleId as string, favorited),
        onMutate: async (favorited) => {
            const detailKey = lifecycleQueryKeys.detail(workspaceId, lifecycleId);
            const listKey = lifecycleQueryKeys.list(workspaceId);

            await Promise.all([
                queryClient.cancelQueries({ queryKey: detailKey }),
                queryClient.cancelQueries({ queryKey: listKey }),
            ]);
            const previousDetail = queryClient.getQueryData<LifecycleInitiative>(detailKey);
            const previousList = queryClient.getQueryData<{ items: LifecycleInitiative[]; nextCursor?: string }>(
                listKey,
            );

            queryClient.setQueryData<LifecycleInitiative>(detailKey, (current) =>
                current ? { ...current, favorited } : current,
            );
            queryClient.setQueryData<{ items: LifecycleInitiative[]; nextCursor?: string }>(listKey, (current) =>
                current
                    ? {
                          ...current,
                          items: current.items.map((item) => (item.id === lifecycleId ? { ...item, favorited } : item)),
                      }
                    : current,
            );

            return { previousDetail, previousList };
        },
        onError: (error, _favorited, context) => {
            captureException(error);

            if (context?.previousDetail) {
                queryClient.setQueryData(lifecycleQueryKeys.detail(workspaceId, lifecycleId), context.previousDetail);
            }
            if (context?.previousList) {
                queryClient.setQueryData(lifecycleQueryKeys.list(workspaceId), context.previousList);
            }
        },
        onSettled: async () => {
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: lifecycleQueryKeys.detail(workspaceId, lifecycleId) }),
                queryClient.invalidateQueries({ queryKey: lifecycleQueryKeys.list(workspaceId) }),
            ]);
        },
    });
};

export const useUpdateLifecycleHierarchyOrder = (lifecycleId?: string) => {
    const workspaceId = useActiveWorkspaceId();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (input: UpdateLifecycleHierarchyOrderInput) =>
            lifecycleService.updateHierarchyOrder(lifecycleId as string, input),
        onMutate: async (input) => {
            const queryKey = lifecycleQueryKeys.detail(workspaceId, lifecycleId);

            await queryClient.cancelQueries({ queryKey });
            const previousLifecycle = queryClient.getQueryData<LifecycleInitiative>(queryKey);

            if (previousLifecycle) {
                const categoryById = new Map(
                    previousLifecycle.taskCategories.map((category) => [category.id, category] as const),
                );
                const groupById = new Map(
                    previousLifecycle.taskCategories.flatMap((category) =>
                        category.taskGroups.map((group) => [group.id, group] as const),
                    ),
                );
                const taskCategories = input.categories.flatMap((categoryOrder) => {
                    const category = categoryById.get(categoryOrder.id);

                    return category
                        ? [
                              {
                                  ...category,
                                  taskGroups: categoryOrder.groupIds.flatMap((groupId) => {
                                      const group = groupById.get(groupId);
                                      return group ? [group] : [];
                                  }),
                              },
                          ]
                        : [];
                });

                queryClient.setQueryData<LifecycleInitiative>(queryKey, {
                    ...previousLifecycle,
                    taskCategories,
                });
            }

            return { previousLifecycle };
        },
        onError: (error, _input, context) => {
            captureException(error);

            if (context?.previousLifecycle) {
                queryClient.setQueryData(
                    lifecycleQueryKeys.detail(workspaceId, lifecycleId),
                    context.previousLifecycle,
                );
            }
        },
        onSuccess: async (lifecycle) => {
            queryClient.setQueryData(lifecycleQueryKeys.detail(workspaceId, lifecycleId), lifecycle);
            await queryClient.invalidateQueries({ queryKey: lifecycleQueryKeys.list(workspaceId) });
        },
    });
};

export const useDeleteLifecycle = () => {
    const workspaceId = useActiveWorkspaceId();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (lifecycleId: string) => lifecycleService.delete(lifecycleId),
        onError: (error) => captureException(error),
        onSuccess: async (_data, lifecycleId) => {
            queryClient.removeQueries({ queryKey: lifecycleQueryKeys.detail(workspaceId, lifecycleId) });
            await queryClient.invalidateQueries({ queryKey: lifecycleQueryKeys.list(workspaceId) });
        },
    });
};

export const useCreateLifecycleAction = (lifecycleId?: string) => {
    const workspaceId = useActiveWorkspaceId();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: Omit<LifecycleApiAction, 'id' | 'revision'>) =>
            lifecycleService.actions.create(lifecycleId as string, input),
        onError: (error) => captureException(error),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: lifecycleQueryKeys.detail(workspaceId, lifecycleId) });
        },
    });
};

export const useUpdateLifecycleAction = (lifecycleId?: string) => {
    const workspaceId = useActiveWorkspaceId();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({
            actionId,
            input,
        }: {
            actionId: string;
            input: Partial<LifecycleApiAction> & { revision: number };
        }) => lifecycleService.actions.update(lifecycleId as string, actionId, input),
        onError: (error) => captureException(error),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: lifecycleQueryKeys.detail(workspaceId, lifecycleId) });
        },
    });
};

export const useDeleteLifecycleAction = (lifecycleId?: string) => {
    const workspaceId = useActiveWorkspaceId();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (actionId: string) => lifecycleService.actions.delete(lifecycleId as string, actionId),
        onError: (error) => captureException(error),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: lifecycleQueryKeys.detail(workspaceId, lifecycleId) });
        },
    });
};

export const useExecuteLifecycleAction = (lifecycleId?: string) => {
    const workspaceId = useActiveWorkspaceId();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ actionId, triggerSnapshot }: { actionId: string; triggerSnapshot?: Record<string, unknown> }) =>
            lifecycleService.actions.execute(lifecycleId as string, actionId, triggerSnapshot),
        onError: (error) => captureException(error),
        onSuccess: async (_execution, variables) => {
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: lifecycleQueryKeys.detail(workspaceId, lifecycleId) }),
                queryClient.invalidateQueries({
                    queryKey: lifecycleQueryKeys.executions(workspaceId, lifecycleId, variables.actionId),
                }),
            ]);
        },
    });
};

export const useLifecycleActionExecutions = (lifecycleId?: string, actionId?: string) => {
    const workspaceId = useActiveWorkspaceId();
    return useQuery<LifecycleActionExecutionRecord[]>({
        enabled: Boolean(workspaceId && lifecycleId && actionId),
        queryFn: () => lifecycleService.actions.executions(lifecycleId as string, actionId as string),
        queryKey: lifecycleQueryKeys.executions(workspaceId, lifecycleId, actionId),
        refetchInterval: (query) =>
            query.state.data?.some((execution) => execution.status === 'Pending' || execution.status === 'Running')
                ? 2_000
                : false,
    });
};

export const useLifecycleCollaborators = (lifecycleId?: string) => {
    const workspaceId = useActiveWorkspaceId();
    const queryClient = useQueryClient();
    const queryKey = lifecycleQueryKeys.collaborators(workspaceId, lifecycleId);
    const query = useQuery({
        enabled: Boolean(workspaceId && lifecycleId),
        queryFn: () => lifecycleService.collaborators.list(lifecycleId as string),
        queryKey,
        staleTime: 15_000,
    });
    const create = useMutation({
        mutationFn: (input: LifecycleCollaboratorInput) =>
            lifecycleService.collaborators.create(lifecycleId as string, input),
        onError: (error) => captureException(error),
        onSuccess: () => queryClient.invalidateQueries({ queryKey }),
    });
    const remove = useMutation({
        mutationFn: (collaboratorId: string) =>
            lifecycleService.collaborators.delete(lifecycleId as string, collaboratorId),
        onError: (error) => captureException(error),
        onSuccess: () => queryClient.invalidateQueries({ queryKey }),
    });
    const update = useMutation({
        mutationFn: ({ collaboratorId, input }: { collaboratorId: string; input: LifecycleCollaboratorUpdateInput }) =>
            lifecycleService.collaborators.update(lifecycleId as string, collaboratorId, input),
        onError: (error) => captureException(error),
        onSuccess: () => queryClient.invalidateQueries({ queryKey }),
    });

    return { ...query, create, remove, update };
};
